import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useChatLocale } from '../../context/ChatThemeContext';
import { useSiteConfig } from '../../context/SiteConfigContext';
import { findBestLocaleValue } from '../../utils/content/localeUtils';

const CHAR_DELAY_MS = 25;

/**
 * Renders the optional booking email disclaimer configured in inchat_booking.email_disclaimer.
 *
 * The text is locale-aware. Supports markdown-style links: [text](url).
 * The entire disclaimer is rendered in the disclaimer font style (italic)
 * with a typewriter reveal animation.
 *
 * Returns null when no disclaimer is configured for the current locale.
 */
const BookingEmailDisclaimer: React.FC = () => {
    const userLocale = useChatLocale();
    const { currentResolver } = useSiteConfig();

    const segments = useMemo(() => {
        const record = currentResolver?.inchat_booking?.email_disclaimer;
        const text = findBestLocaleValue(record, userLocale);
        if (typeof text !== 'string' || !text.trim()) return null;
        const parsed = parseDisclaimerSegments(text.trim());
        return parsed.some((s) => s.text) ? parsed : null;
    }, [currentResolver, userLocale]);

    const fullText = useMemo(
        () => (segments ? segments.map((s) => s.text).join('') : ''),
        [segments]
    );

    const [charCount, setCharCount] = useState(0);

    useEffect(() => {
        if (!fullText) return;
        setCharCount(0);
        let frame: number;
        let start: number | null = null;
        const totalChars = fullText.length;

        const step = (ts: number) => {
            if (start === null) start = ts;
            const elapsed = ts - start;
            const next = Math.min(Math.floor(elapsed / CHAR_DELAY_MS) + 1, totalChars);
            setCharCount(next);
            if (next < totalChars) {
                frame = requestAnimationFrame(step);
            }
        };
        frame = requestAnimationFrame(step);
        return () => cancelAnimationFrame(frame);
    }, [fullText]);

    if (!segments) return null;

    return (
        <div
            className="rose-widget-booking-disclaimer w-full text-center text-xs italic leading-4 select-none px-2 py-1"
            style={{ color: 'var(--rw-text-disclaimer)' }}
        >
            {renderSegmentsWithLimit(segments, charCount)}
        </div>
    );
};

interface Segment {
    type: 'plain' | 'link';
    text: string;
    href?: string;
}

/** Render segments but only show the first `charLimit` characters total (typewriter). */
function renderSegmentsWithLimit(segments: Segment[], charLimit: number) {
    let remaining = charLimit;
    const elements: React.ReactNode[] = [];

    for (let i = 0; i < segments.length; i++) {
        if (remaining <= 0) break;
        const seg = segments[i];
        const visible = seg.text.slice(0, remaining);
        remaining -= visible.length;

        if (seg.type === 'link') {
            elements.push(
                <a
                    key={i}
                    href={seg.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rose-widget-booking-disclaimer-link underline"
                >
                    {visible}
                </a>
            );
        } else {
            elements.push(<span key={i}>{visible}</span>);
        }
    }

    return elements;
}

/**
 * Parse disclaimer text into plain text and markdown link segments.
 * Supports [text](url) syntax. All HTML tags are stripped (text content kept).
 */
function parseDisclaimerSegments(raw: string): Segment[] {
    const cleaned = stripTags(raw);
    const segments: Segment[] = [];
    const re = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while (true) {
        match = re.exec(cleaned);
        if (match === null) break;
        const before = cleaned.slice(lastIndex, match.index);
        if (before) segments.push({ type: 'plain', text: before });
        segments.push({ type: 'link', text: match[1], href: match[2] });
        lastIndex = re.lastIndex;
    }

    const remaining = cleaned.slice(lastIndex);
    if (remaining) segments.push({ type: 'plain', text: remaining });

    return segments;
}

/** Remove all HTML tags, keeping their text content. */
function stripTags(s: string): string {
    return s.replace(/<[^>]*>/g, '');
}

export default BookingEmailDisclaimer;
